// src/main.jsx
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { IntercomProvider } from 'react-use-intercom';
import routes from './router/routes';
import './utils/i18n';
import './index.css';
import 'virtual:svg-icons-register';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';
import { initLanguageSync, cleanupLanguageSync } from './utils/languageSync';
import { Toaster } from 'sonner';
import { queryClient } from './lib/react-query';
import { initPostHog } from './lib/posthog';
import { registerPreloadErrorHandler } from './utils/lazyWithRetry';
import IntercomMessenger from '@/components/common/IntercomMessenger';

initPostHog();
registerPreloadErrorHandler();

// App ID public Intercom - configurable par env pour staging/prod (VITE_INTERCOM_APP_ID)
const INTERCOM_APP_ID =
  (import.meta.env.VITE_INTERCOM_APP_ID && import.meta.env.VITE_INTERCOM_APP_ID.trim()) ||
  'a7rb5ne8';

function AppRouter() {
  const element = useRoutes(routes);

  return (
    <>
      <IntercomMessenger />
      {element}
    </>
  );
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    initLanguageSync();
    return () => cleanupLanguageSync();
  }, []);

  useEffect(() => {
    if (i18n.language === 'fr') {
      dayjs.locale('fr');
    } else {
      dayjs.locale('en');
    }
  }, [i18n.language]);

  return (
    <>
      <Toaster
        position="top-center"
        top={94}
        duration={3000}
        richColors
        closeButton
        expand={true}
        toastOptions={{
          style: {
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            zIndex: 2147483647, // Maximum z-index pour passer devant tout
          },
          classNames: {
            toast: 'relative',
            title: 'font-semibold text-sm text-gray-800 pr-8',
            description: 'text-xs text-gray-500 mt-1 pr-8',
            actionButton: 'text-xs font-medium px-3 py-1.5 rounded-md transition-all',
            cancelButton: 'text-xs font-medium px-3 py-1.5 rounded-md transition-all',
            closeButton: 'absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors',
          },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <IntercomProvider appId={INTERCOM_APP_ID} autoBoot={false}>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </IntercomProvider>
      </QueryClientProvider>
    </>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

/** @type {import('react-dom/client').Root | undefined} */
let reactRoot = globalThis.__FOLKYN_REACT_ROOT__;
if (!reactRoot) {
  reactRoot = ReactDOM.createRoot(rootElement);
  globalThis.__FOLKYN_REACT_ROOT__ = reactRoot;
}

reactRoot.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

if (import.meta.hot) {
  import.meta.hot.accept();
}
